import { ReactNode, useState } from 'react';
import { IonText } from '@ionic/react';
import { Button } from '../components/button/Button.tsx';
import { IDialogBaseProps } from '../components/dialog/DialogPresenter.tsx';
import { FloatingBackButton } from '../components/button/FloatingBackButton.tsx';
import { FloatingCloseButton } from '../components/button/FloatingCloseButton.tsx';
import { AuthService } from './AuthService.ts';
import { EmailIcon, FacebookIcon, GoogleIcon, AppleIcon } from '../components/icons/Icons.tsx';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { DevSettings } from '../support/DevSettings.ts';
import { TrackingService } from '../support/tracking/TrackingService.ts';
import { Device } from '../support/Device.ts';
import { AutoPylotPrincipal } from './AutoPylotPrincipal.ts';
import { useBreakpoints } from '../support/hooks/useBreakpoints.tsx';

export type LoginAction = { key: string, Icon: any, text: string,  handler: () => Promise<boolean> };

export interface ILoginDialogPresenter {
    showLoginDialog: (params: { message?: ReactNode, imageUrl?: string, returnTo?: string }) => Promise<void>
}

export const LoginDialog: React.FC<IDialogBaseProps & { message?: ReactNode, imageUrl?: string, returnTo?: string }> = ({ onDismiss, message = <>To continue, please sign up for free,<br/>no password required!</>, imageUrl = '/assets/images/auth/auth-incentive-v1.svg', returnTo }) => {
    const { isAboveBreakpoint } = useBreakpoints();
    const isFullScreen = !isAboveBreakpoint('sm');
    const isDesktopModal = isAboveBreakpoint('md');

    const authService = useInstance(AuthService);
    const deviceInfo = useInstance(Device);
    const devSettings = useInstance(DevSettings);
    const tracker = useInstance(TrackingService);
    const principal = useInstance(AutoPylotPrincipal);

    const [working, setWorking] = useState(false);

    if (principal.email){
        // prevent the dialog from showing, to prevent downstream issue when trying to exchange their "guest" access token for a email access token
        throw new Error(`Attempting to show the login dialog for a user that is not a guest. ${JSON.stringify({ userId: principal.userId, email: principal.email })}.`);
    }

    const actions: LoginAction[] = [
        {
            key: 'google',
            text: 'Continue with Google',
            Icon: GoogleIcon,
            handler: async () => {
                return await authService.login({ connection: 'google-oauth2', returnTo });
            }
        },
        {
            key: 'email',
            text: 'Continue with Email',
            Icon: EmailIcon,
            handler: async () => {
                return await authService.login({ connection: 'email', returnTo });
            }
        }
    ];

    if (deviceInfo.platform !== 'android') {
        actions.unshift({
            key: 'apple',
            text: 'Continue with Apple',
            Icon: AppleIcon,
            handler: async () => {
                return await authService.login({ connection: 'apple', returnTo });
            }
        });
    }

    if (devSettings.enableFacebookLogin) {
        actions.unshift(
            {
                key: 'facebook',
                text: 'Continue with Facebook',
                Icon: FacebookIcon,
                handler: async () => {
                    return await authService.login({ connection: 'facebook', returnTo });
                }
            }
        );
    }
    const onAction = async ({ key, handler }: { key: string, handler: () => Promise<boolean> }) => {
        tracker.track('Auth Options Continued', () => {

            // screenVariant is imageUrl with path and extension stripped
            // e.g. "/assets/images/auth/auth-incentive-v1.svg" -> "auth-incentive-v1"
            const screenVariant = imageUrl.split('/').pop()?.split('.')[0] || 'unknown';

            return {
                with: key,
                screenVariant
            };
        });

        // theoretically, the handler resolves with a boolean indicating whether the login was successful, but
        // currently all handlers reload the app after redirecting, resolving only if the user aborted the flow
        // (by failing to consent or clicking cancel), so simply awaiting here is all we need to do
        setWorking(true);
        await handler();
        setWorking(false);
    };

    // wrap in full-page div that sets opacity 0 while "working" (with 2s delay to allow time for browser window to open).
    // since handlers only resolve if auth fails, this ensures dialog is not visible again briefly when browser window closes.
    return (
        <div style={{position: 'relative', width: '100%', height: '100%', opacity: working ? 0 : 1, transition: working ? 'opacity 0s 2s' : 'none'}}>

            {!isDesktopModal &&
                <FloatingBackButton onClick={onDismiss} />
            }
            {isDesktopModal &&
                <FloatingCloseButton onClick={onDismiss} />
            }

            <div style={{height: '100%', padding: 'max(var(--ion-safe-area-top), 15px) 20px 20px', display: 'flex', flexDirection: 'column', gap: 15, justifyContent: 'space-around', alignItems: 'center'}} data-id="login-dialog">

                <img src="./assets/images/logo-name-horiz.png" alt="AutoPylot logo" width="180px" />

                {imageUrl &&
                    <img style={{maxHeight: isDesktopModal ? 220 : 450}} src={imageUrl} alt="" />
                }

                <div style={{width: '100%', display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'center'}}>

                    <IonText color="dark" style={{fontSize: 18}}><b>{message}</b></IonText>

                    {actions.map(({ key, Icon, text, handler }) => (
                        <Button dataType={`continue-${key}`} key={text} fill="outline" minWidth={isFullScreen ? undefined : 340} expand={isFullScreen ? 'block' : undefined} onClick={() => onAction({ key, handler })}>
                            <span style={{position: 'absolute', left: 15}}><Icon color="dark" size="medium" /></span>
                            <b>{text}</b>
                        </Button>
                    ))}
                </div>

            </div>

        </div>
    );
};
